import type { EChartsOption, TooltipComponentFormatterCallbackParams } from 'echarts';
import { soccerZToChartZ } from 'pages/MeasurementDetail/coords';
import { darkTooltip } from 'utilities/chartUtils';
import { t } from 'utilities/TranslationUtils';
import type { FreeKickZoneDto, MeasurementMarkerDto } from 'types/models/SessionsPageData';
import { getFreeKickZoneIdForLayout, type ZoneStat } from './playerPerformanceUtils';
import { zoneColorFromStat } from './cornerZoneScoreColor';
import {
  GOAL_POST,
  GOAL_DEPTH,
  P_BOX_Z,
  P_BOX_X,
  SIX_BOX_Z,
  SIX_BOX_X,
  PENALTY_SPOT_X,
  ARC_RADIUS,
  PITCH_GREEN,
  LINE_COLOR,
  NET_COLOR,
  LINE_WIDTH_MAJOR,
  LINE_WIDTH_FRAME,
  NET_LINE_WIDTH,
} from 'pages/Sessions/charts/topViewPitchOptions';

/** Data item carried on a zone `custom` series entry — read back by the tooltip formatter. */
interface ZoneSeriesDataItem {
  value: number;
  zoneId: number;
  kickCount: number;
  sessionCount: number;
  onTargetPct: number | null;
}

/** Per-point data item carried on the `fk-kicks` scatter series. */
interface KickPointDataItem {
  value: [number, number];
  itemStyle: {
    color: string;
    opacity: number;
    borderColor: string;
    borderWidth: number;
  };
}

const ZONE_SERIES_ID_PATTERN = /^fk-zone-\d+$/;

/**
 * Tooltip formatter for the zone pitch chart. Only zone polygon series (`fk-zone-<id>`) carry
 * per-zone stats — every other series (pitch lines, kicks, count labels) is silenced here so
 * hovering them never shows a tooltip.
 */
function zoneTooltipFormatter(rawParams: TooltipComponentFormatterCallbackParams): string {
  const params = Array.isArray(rawParams) ? rawParams[0] : rawParams;
  const seriesId = params?.seriesId;
  if (typeof seriesId !== 'string' || !ZONE_SERIES_ID_PATTERN.test(seriesId)) return '';

  const data = params.data as ZoneSeriesDataItem | undefined;
  if (!data) return '';

  if (!data.kickCount) {
    return t('playerPerformance.zoneTooltipNoData');
  }

  const onTargetLabel = data.onTargetPct != null ? `${Math.round(data.onTargetPct)}%` : '—';
  return [
    `${t('playerPerformance.zoneTooltipSessions')}: ${data.sessionCount}`,
    `${t('playerPerformance.zoneTooltipKicks')}: ${data.kickCount}`,
    `${t('playerPerformance.zoneTooltipOnTarget')}: ${onTargetLabel}`,
  ].join('<br/>');
}

/** Score-type driven zone color (Good/Average/Bad/None). */
export function zoneFillColor(stat: ZoneStat | undefined): string {
  return zoneColorFromStat(stat);
}

/** Convert a football-coord outline [x,z] to chart tuples [chartZ, x] (z flipped for 2D view). */
export function toChartPolygon(outline: [number, number][]): [number, number][] {
  return outline.map(([x, z]) => [soccerZToChartZ(z), x]);
}

/** Average of a polygon's chart-space points — used to anchor the per-zone kick-count label. */
export function polygonCentroid(polygon: [number, number][]): [number, number] {
  const total = polygon.reduce<[number, number]>((acc, [z, x]) => [acc[0] + z, acc[1] + x], [0, 0]);
  const n = polygon.length || 1;
  return [total[0] / n, total[1] / n];
}

// The zone view's axis window only needs to cover the goal + penalty-box area
// (xAxis/z: [-34, 34], yAxis/x depth: [-3, 35]) — not the full half-pitch.
const ZONE_VIEW_Z = 34;
const ZONE_VIEW_X_MAX = 35;
const FIELD_LEFT = -ZONE_VIEW_Z + 1;
const FIELD_RIGHT = ZONE_VIEW_Z - 1;
const FIELD_TOP = 0;

/** Vertical net lines across the goal opening for visual depth. */
function buildGoalNetLines(): Array<[[number, number], [number, number]]> {
  const segments: Array<[[number, number], [number, number]]> = [];
  const netSpacing = 0.4; // Line spacing for net texture
  
  // Vertical mesh lines
  for (let z = -GOAL_POST; z <= GOAL_POST; z += netSpacing) {
    segments.push([[z, 0], [z, -GOAL_DEPTH]]);
  }
  
  // Horizontal mesh lines (parallel to goal line)
  const depthSteps = Math.ceil(GOAL_DEPTH / netSpacing);
  for (let i = 1; i < depthSteps; i++) {
    const depth = -((i / depthSteps) * GOAL_DEPTH);
    segments.push([[-GOAL_POST, depth], [GOAL_POST, depth]]);
  }
  
  return segments;
}

function pitchLineSeries(id: string, data: Array<[number, number]> | (([number, number] | null))[], color = LINE_COLOR, width = LINE_WIDTH_MAJOR, zIndex = 2) {
  return {
    id,
    type: 'line' as const,
    data,
    symbol: 'none' as const,
    lineStyle: { color, width },
    silent: true,
    animation: false,
    z: zIndex,
  };
}

/** Quarter circle (the "D" arc at the top of the penalty box) as a poly-line in chart coords. */
function buildPenaltyArc(): [number, number][] {
  const arcAngleCutoff = Math.acos((P_BOX_X - PENALTY_SPOT_X) / ARC_RADIUS);
  const pts: [number, number][] = [];
  for (let i = 0; i <= 60; i++) {
    const a = -arcAngleCutoff + (2 * arcAngleCutoff) * (i / 60);
    pts.push([ARC_RADIUS * Math.sin(a), PENALTY_SPOT_X + ARC_RADIUS * Math.cos(a)]);
  }
  return pts;
}

/**
 * Grass + white pitch-line series rendered UNDER the zone polygons, so the zone view shows the
 * goal + penalty box instead of floating on white. Self-contained (no dependency on / changes to
 * SessionsPitchChart) — see the module comment above.
 */
export function buildPitchBaseSeries(fieldBounds?: { left: number; right: number; top: number }) {
  const fieldLeft = fieldBounds?.left ?? FIELD_LEFT;
  const fieldRight = fieldBounds?.right ?? FIELD_RIGHT;
  const fieldTop = fieldBounds?.top ?? FIELD_TOP;
  const arcPts = buildPenaltyArc();
  const netLines = buildGoalNetLines();

  return [
    pitchLineSeries('fk-pitch-goal-back', [[-GOAL_POST, -GOAL_DEPTH], [GOAL_POST, -GOAL_DEPTH]], '#fff', LINE_WIDTH_FRAME),
    pitchLineSeries('fk-pitch-goal-post-l', [[-GOAL_POST, 0], [-GOAL_POST, -GOAL_DEPTH]], '#fff', LINE_WIDTH_FRAME),
    pitchLineSeries('fk-pitch-goal-post-r', [[GOAL_POST, 0], [GOAL_POST, -GOAL_DEPTH]], '#fff', LINE_WIDTH_FRAME),
    pitchLineSeries('fk-pitch-goal-line', [[fieldLeft, fieldTop], [fieldRight, fieldTop]], '#fff', LINE_WIDTH_FRAME),
    // Goal net mesh (vertical, horizontal, and diagonal lines)
    ...netLines.map((segment, idx) =>
      pitchLineSeries(`fk-pitch-goal-net-${idx}`, segment, NET_COLOR, NET_LINE_WIDTH, 2)
    ),
    pitchLineSeries('fk-pitch-six-bottom', [[-SIX_BOX_Z, SIX_BOX_X], [SIX_BOX_Z, SIX_BOX_X]]),
    pitchLineSeries('fk-pitch-six-left', [[-SIX_BOX_Z, 0], [-SIX_BOX_Z, SIX_BOX_X]]),
    pitchLineSeries('fk-pitch-six-right', [[SIX_BOX_Z, 0], [SIX_BOX_Z, SIX_BOX_X]]),
    pitchLineSeries('fk-pitch-p-bottom', [[-P_BOX_Z, P_BOX_X], [P_BOX_Z, P_BOX_X]]),
    pitchLineSeries('fk-pitch-p-left', [[-P_BOX_Z, 0], [-P_BOX_Z, P_BOX_X]]),
    pitchLineSeries('fk-pitch-p-right', [[P_BOX_Z, 0], [P_BOX_Z, P_BOX_X]]),
    pitchLineSeries('fk-pitch-arc', arcPts),
    {
      id: 'fk-pitch-spot',
      type: 'scatter' as const,
      data: [[0, PENALTY_SPOT_X]] as [number, number][],
      symbolSize: 5,
      itemStyle: { color: '#fff' },
      silent: true,
      animation: false,
      z: 2,
    },
  ];
}

// yAxis minimum for the compact (default) view — hides the goal and near-goal area so the chart
// focuses on the zone region. The expanded view restores the full range (yMin = -3 shows the goal).
export const COMPACT_Y_MIN = 13;

/** Visible data window of the zone pitch (xAxis is the first polygon coordinate, yAxis the second). */
const VISIBLE_Z_MIN = -24;
const VISIBLE_Z_MAX = 24;
const VISIBLE_X_MAX = 31;

type Point = [number, number];

/** Sutherland–Hodgman clip of a polygon against one axis-aligned half-plane. */
function clipHalfPlane(poly: Point[], axis: 0 | 1, limit: number, keepAbove: boolean): Point[] {
  const inside = (p: Point) => (keepAbove ? p[axis] >= limit : p[axis] <= limit);
  const out: Point[] = [];
  poly.forEach((cur, i) => {
    const prev = poly[(i + poly.length - 1) % poly.length];
    if (inside(cur) !== inside(prev)) {
      const t = (limit - prev[axis]) / (cur[axis] - prev[axis]);
      out.push([prev[0] + (cur[0] - prev[0]) * t, prev[1] + (cur[1] - prev[1]) * t]);
    }
    if (inside(cur)) out.push(cur);
  });
  return out;
}

/** The part of a zone polygon that is actually on screen for the given yAxis minimum. */
export function visibleZonePolygon(polygon: Point[], yMin: number): Point[] {
  let poly = polygon;
  poly = clipHalfPlane(poly, 0, VISIBLE_Z_MIN, true);
  poly = clipHalfPlane(poly, 0, VISIBLE_Z_MAX, false);
  poly = clipHalfPlane(poly, 1, yMin, true);
  poly = clipHalfPlane(poly, 1, VISIBLE_X_MAX, false);
  return poly;
}

function distanceToSegment([px, py]: Point, [ax, ay]: Point, [bx, by]: Point): number {
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSq = dx * dx + dy * dy;
  const t = lengthSq === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lengthSq));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

function insidePolygon([px, py]: Point, poly: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i, i += 1) {
    const [xi, yi] = poly[i];
    const [xj, yj] = poly[j];
    if ((yi > py) !== (yj > py) && px < ((xj - xi) * (py - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/**
 * The visual centre of a zone's visible polygon: the point deepest inside it (furthest from every edge), found by sampling
 * a grid and refining around the best point. Unlike a centroid it stays inside a curved band, and unlike a mid-radius point
 * it follows the part that is actually on screen, so a label sits in the middle of what the user sees. Null when empty.
 */
export function polygonVisualCentre(polygon: Point[]): Point | null {
  if (polygon.length < 3) return null;
  const edgeDistance = (p: Point) => polygon.reduce((min, cur, i) => Math.min(min, distanceToSegment(p, polygon[(i + polygon.length - 1) % polygon.length], cur)), Infinity);
  let [minZ, maxZ, minX, maxX] = [Infinity, -Infinity, Infinity, -Infinity];
  polygon.forEach(([z, x]) => { minZ = Math.min(minZ, z); maxZ = Math.max(maxZ, z); minX = Math.min(minX, x); maxX = Math.max(maxX, x); });

  let best: Point | null = null;
  let bestDistance = -1;
  let [fromZ, toZ, fromX, toX] = [minZ, maxZ, minX, maxX];
  const steps = 24;
  for (let round = 0; round < 3; round += 1) {
    const stepZ = (toZ - fromZ) / steps;
    const stepX = (toX - fromX) / steps;
    for (let i = 0; i <= steps; i += 1) {
      for (let j = 0; j <= steps; j += 1) {
        const candidate: Point = [fromZ + i * stepZ, fromX + j * stepX];
        if (!insidePolygon(candidate, polygon)) continue;
        // Equally deep points (the whole spine of a long rectangle) tie: prefer the one nearest the middle of the zone.
        const distance = edgeDistance(candidate) - 0.02 * Math.hypot(candidate[0] - (minZ + maxZ) / 2, candidate[1] - (minX + maxX) / 2);
        if (distance > bestDistance) { best = candidate; bestDistance = distance; }
      }
    }
    if (!best) return null;
    [fromZ, toZ, fromX, toX] = [best[0] - stepZ, best[0] + stepZ, best[1] - stepX, best[1] + stepX];
  }
  return best;
}

export function buildFreeKickZoneOption(args: {
  zones: FreeKickZoneDto[];
  stats: Map<number, ZoneStat>;
  kicks: MeasurementMarkerDto[];
  selected: Set<number>;
  /** Minimum yAxis value: COMPACT_Y_MIN (13) hides the goal and near-goal area; -3 shows them. */
  yMin?: number;
  /** Layout whose zone ids key `stats` and the kicks. Defaults to layout 1. */
  layoutId?: string;
}): EChartsOption {
  const { zones, stats, kicks, selected, yMin = COMPACT_Y_MIN, layoutId = '1' } = args;

  const pitchBaseSeries = buildPitchBaseSeries();
  const selectionActive = selected.size > 0;

  const zoneSeries = zones.map((z) => {
    const isSel = selected.has(z.zoneId);
    const stat = stats.get(z.zoneId);
    const dataItem: ZoneSeriesDataItem = {
      value: 0,
      zoneId: z.zoneId,
      kickCount: stat?.kickCount ?? 0,
      sessionCount: stat?.sessionCount ?? 0,
      onTargetPct: stat?.onTargetPct ?? null,
    };
    return {
      id: `fk-zone-${z.zoneId}`,
      type: 'custom' as const,
      // Rendered via renderItem into the cartesian grid; see component note.
      z: 1,
      silent: false,
      animation: false,
      data: [dataItem],
      polygon: toChartPolygon(z.outline),
      // Label placement comes from the BFF (layout-specific); the renderer only needs these.
      geometry: { innerRadius: z.innerRadius, outerRadius: z.outerRadius, centreAngle: z.centreAngle, halfSpan: z.halfSpan },
      itemStyle: {
        color: zoneFillColor(stats.get(z.zoneId)),
        opacity: !selectionActive ? 0.6 : (isSel ? 0.9 : 0.2),
        borderColor: !selectionActive ? 'rgba(255,255,255,0.35)' : (isSel ? '#ffffff' : 'rgba(255,255,255,0.15)'),
        borderWidth: !selectionActive ? 1 : (isSel ? 3 : 1),
      },
    };
  });

  const selectedZoneId = selectionActive ? Array.from(selected)[0] : undefined;

  const kickPoints: KickPointDataItem[] = kicks
    .filter((m) => {
      if (m.kickX == null || m.kickZ == null) return false;
      return true;
    })
    .map((m) => {
      const kickZoneId = getFreeKickZoneIdForLayout(m, layoutId);
      return {
      value: [soccerZToChartZ(m.kickZ as number), m.kickX as number],
      itemStyle: {
        color: zoneFillColor(kickZoneId != null ? stats.get(kickZoneId) : undefined),
        opacity: !selectionActive || kickZoneId === selectedZoneId ? 0.9 : 0.2,
        borderColor: 'rgba(0,0,0,0.45)',
        borderWidth: 1,
      },
    };
    });

  const kickSeries = {
    id: 'fk-kicks',
    type: 'scatter' as const,
    z: 3,
    symbolSize: 8,
    animation: false,
    silent: false,
    data: kickPoints,
  };

  return {
    backgroundColor: PITCH_GREEN,
    tooltip: { ...darkTooltip('item'), formatter: zoneTooltipFormatter },
    grid: { left: 0, right: 0, top: 0, bottom: 0, containLabel: false },
    xAxis: { type: 'value', min: VISIBLE_Z_MIN, max: VISIBLE_Z_MAX, show: false },
    yAxis: { type: 'value', min: yMin, max: VISIBLE_X_MAX, inverse: true, show: false },
    animationDuration: 0,
    series: [...pitchBaseSeries, ...zoneSeries, kickSeries] as EChartsOption['series'],
  };
}
